// What a refused draft confirm means for the card (v3 `orpheusDraftConfirm.ts`, KUR-1). The confirm
// door — `POST …/chat/drafts/<draft_ref>/confirm/` — takes no body, applies the draft exactly as it
// was previewed, at most once, and answers every refusal with `{ error: { code, message, details } }`.
import { ApiError } from '@/shared/http'

export interface DraftRowError {
  field: string
  code: string
  message: string
}

export type DraftConfirmFailureKind =
  | 'already_saved'
  | 'superseded'
  | 'spent'
  | 'refused'
  | 'not_found'
  | 'consent'
  | 'permission'
  | 'generic'

export interface DraftConfirmFailure {
  kind: DraftConfirmFailureKind
  /** Our sentence, as an i18n key — used whenever the backend sent none of its own. */
  messageKey: string
  /** The backend's own sentence for a refusal (ORPHEUS_DRAFT_REFUSED); empty otherwise. */
  message: string
  reason: string
  rowErrors: DraftRowError[]
}

const K = 'orpheus.setup.failure'

function failure(kind: DraftConfirmFailureKind, key: string): DraftConfirmFailure {
  return { kind, messageKey: `${K}.${key}`, message: '', reason: '', rowErrors: [] }
}

function details(error: ApiError): Record<string, unknown> {
  const body = error.payload
  const envelope =
    body && typeof body === 'object' ? (body as Record<string, unknown>).error : undefined
  const raw =
    envelope && typeof envelope === 'object'
      ? (envelope as Record<string, unknown>).details
      : undefined
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

function envelopeMessage(error: ApiError): string {
  const body = error.payload
  const envelope =
    body && typeof body === 'object' ? (body as Record<string, unknown>).error : undefined
  const message =
    envelope && typeof envelope === 'object'
      ? (envelope as Record<string, unknown>).message
      : undefined
  return typeof message === 'string' ? message : ''
}

/** A draft that can no longer be saved: replaced by a newer one, or spent by an earlier press. */
export function spentDraftFailure(draftStatus: string): DraftConfirmFailure {
  return draftStatus === 'superseded'
    ? failure('superseded', 'superseded')
    : failure('spent', 'spent')
}

/**
 * A draft is single-use: once the backend claimed it, any answer but success leaves it spent, so
 * only a fresh draft can be saved.
 */
export function draftConfirmFailure(cause: unknown): DraftConfirmFailure {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  const info = details(error)

  if (error.code === 'ORPHEUS_DRAFT_NOT_PENDING') {
    const draftStatus = typeof info.draft_status === 'string' ? info.draft_status : ''
    if (draftStatus === 'applied' || draftStatus === 'applying') {
      return failure('already_saved', 'alreadySaved')
    }
    return spentDraftFailure(draftStatus)
  }
  if (error.code === 'ORPHEUS_DRAFT_REFUSED') {
    const rowErrors = (Array.isArray(info.row_errors) ? info.row_errors : [])
      .filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object')
      .map((row) => ({
        field: typeof row.field === 'string' ? row.field : '',
        code: typeof row.code === 'string' ? row.code : '',
        message: typeof row.message === 'string' ? row.message : '',
      }))
    return {
      ...failure('refused', 'generic'),
      message: envelopeMessage(error),
      reason: typeof info.reason === 'string' ? info.reason : '',
      rowErrors,
    }
  }
  if (error.code === 'ORPHEUS_DRAFT_NOT_FOUND' || error.status === 404) {
    return failure('not_found', 'notFound')
  }
  if (error.code === 'ORPHEUS_CONSENT_REQUIRED') return failure('consent', 'consent')
  if (error.code === 'PERMISSION_DENIED' || error.status === 403) {
    return failure('permission', 'permission')
  }
  return failure('generic', 'generic')
}
